import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Target,
  Plus,
  Trash2,
  Calculator,
  Trophy,
  AlertTriangle,
  Sparkles,
  Loader2,
  BookOpen,
  ChevronDown,
} from 'lucide-react';
import { projectAnalysisApi, type ProjectOption } from '../lib/api/projectAnalysis';
import {
  bidDomainApi,
  type BidDomainResult,
  type BidGuideline,
  type BidderInput,
  type BidderResult,
} from '../lib/api/bidDomain';
import ProjectSearchSelect from './common/ProjectSearchSelect';
import BidDomainAiPanel from './BidDomainAiPanel';
import {
  formatDisplayNumber,
  formatNumberInput,
  parseNumberInput,
  parseNumberToFloat,
} from '../lib/numberFormat';

const emptyBidder = (isSelf = false): BidderInput => ({ name: '', price: '', is_self: isSelf });

function applyAnalysisToSelfBidder(
  bidders: BidderInput[],
  totalCost: string,
  companyName?: string
): BidderInput[] {
  const price = parseNumberInput(String(totalCost));
  const selfIdx = bidders.findIndex((b) => b.is_self);
  const idx = selfIdx >= 0 ? selfIdx : 0;
  return bidders.map((b, i) =>
    i === idx
      ? {
          ...b,
          price,
          name: companyName?.trim() || b.name,
          is_self: true,
        }
      : b
  );
}

function renderDomainStatus(b: BidderResult, result: BidDomainResult) {
  if (result.needs_committee_review && result.article === '6-2') {
    return (
      <span className="text-amber-700 text-xs" title={b.excluded_reason ?? ''}>
        ارجاع کمیسیون
      </span>
    );
  }

  if (b.in_domain) {
    return (
      <span className="text-emerald-700 text-xs font-bold" title={b.compatibility_method ?? undefined}>
        متناسب
        {b.compatibility_method && (
          <span className="block text-[10px] font-normal text-emerald-600 mt-0.5">{b.compatibility_method}</span>
        )}
      </span>
    );
  }

  return (
    <span className="text-red-600 text-xs" title={b.excluded_reason ?? ''}>
      غیرمتناسب
      {b.excluded_reason && (
        <span className="block text-[10px] text-slate-500 mt-0.5">{b.excluded_reason}</span>
      )}
    </span>
  );
}

const BidDomainPage: React.FC = () => {
  const [guidelines, setGuidelines] = useState<BidGuideline[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [selectedProject, setSelectedProject] = useState<ProjectOption | undefined>();
  const [analysisId, setAnalysisId] = useState<number | ''>('');
  const [analyses, setAnalyses] = useState<Array<{ id: number; name: string; total_cost: string }>>([]);
  const [companyName, setCompanyName] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [guidelineId, setGuidelineId] = useState<number | ''>('');
  const [Po, setPo] = useState('');
  const [bidders, setBidders] = useState<BidderInput[]>([emptyBidder(true), emptyBidder()]);
  const [result, setResult] = useState<BidDomainResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiReply, setAiReply] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    bidDomainApi.guidelines().then((r) => {
      setGuidelines(r.data ?? []);
      if (r.data?.[0]) setGuidelineId(r.data[0].id);
    }).catch(() => {});
    projectAnalysisApi.projects().then((r) => setProjects(r.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (projectId === '') {
      setAnalyses([]);
      setAnalysisId('');
      setCompanyName('');
      return;
    }
    setContextLoading(true);
    bidDomainApi.projectContext(Number(projectId)).then((r) => {
      const list = r.data?.analyses ?? [];
      const name = r.data?.company_name ?? '';
      setAnalyses(list);
      setCompanyName(name);
      if (list.length > 0) {
        const latest = list[0];
        setAnalysisId(latest.id);
        setBidders((prev) => applyAnalysisToSelfBidder(prev, String(latest.total_cost), name));
      } else {
        setAnalysisId('');
      }
    }).catch(() => {}).finally(() => setContextLoading(false));
  }, [projectId]);

  const handleProjectChange = (id: number | '', project?: ProjectOption) => {
    setProjectId(id);
    setSelectedProject(project);
    if (id === '') {
      setAnalyses([]);
      setAnalysisId('');
      setCompanyName('');
    }
  };

  const handleAnalysisChange = (id: number | '') => {
    setAnalysisId(id);
    if (id === '') return;
    const a = analyses.find((x) => x.id === id);
    if (a?.total_cost) {
      setBidders((prev) => applyAnalysisToSelfBidder(prev, String(a.total_cost), companyName));
    }
  };

  const updateBidder = (i: number, patch: Partial<BidderInput>) => {
    setBidders((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };

  const setSelfBidder = (i: number) => {
    setBidders((prev) => prev.map((b, idx) => ({ ...b, is_self: idx === i })));
  };

  const applyAiBidders = useCallback((suggested: BidderInput[], po?: string) => {
    if (suggested.length > 0) {
      setBidders(
        suggested.map((b) => ({
          ...b,
          price: parseNumberInput(b.price) || b.price,
        }))
      );
    }
    if (po) setPo(parseNumberInput(po) || po);
    setError(null);
  }, []);

  const handleCalculate = async () => {
    if (!guidelineId || !Po.trim()) {
      setError('دستورالعمل و مبلغ بهنگام الزامی است.');
      return;
    }
    setLoading(true);
    setError(null);
    setAiReply(null);
    try {
      const parsedBidders = bidders
        .map((b) => ({
          name: b.name.trim() || undefined,
          price: parseNumberToFloat(b.price),
          is_self: b.is_self,
        }))
        .filter((b) => b.price > 0);

      const res = await bidDomainApi.calculate({
        guideline_id: Number(guidelineId),
        estimate_at_tender: parseNumberToFloat(Po),
        bidders: parsedBidders,
        project_id: projectId !== '' ? Number(projectId) : undefined,
        cost_analysis_id: analysisId !== '' ? Number(analysisId) : undefined,
      });
      setResult(res.result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در محاسبه');
    } finally {
      setLoading(false);
    }
  };

  const handleAiAdvice = useCallback(async () => {
    if (!result || !guidelineId || !Po.trim()) return;
    setAiLoading(true);
    setError(null);
    try {
      const parsedBidders = bidders
        .map((b) => ({
          name: b.name.trim() || undefined,
          price: parseNumberToFloat(b.price),
          is_self: b.is_self,
        }))
        .filter((b) => b.price > 0);

      const res = await bidDomainApi.aiAdvice({
        guideline_id: Number(guidelineId),
        estimate_at_tender: parseNumberToFloat(Po),
        bidders: parsedBidders,
        result,
        question: parsedBidders.length === 0
          ? 'فقط قیمت بهنگام را دارم. طبق بخشنامه چه مراحلی اعمال می‌شود؟'
          : 'نتیجه محاسبه را طبق ماده ۶ و ۷ توضیح بده.',
      });
      if (res.reply) setAiReply(res.reply);
      else if (res.message) setError(res.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در مشاوره AI');
    } finally {
      setAiLoading(false);
    }
  }, [result, guidelineId, Po, bidders]);

  const selectedGuideline = useMemo(
    () => guidelines.find((g) => g.id === guidelineId),
    [guidelines, guidelineId]
  );

  return (
    <div className="flex flex-col h-full min-h-0" dir="rtl">
      {/* هدر ثابت */}
      <div className="shrink-0 px-4 py-3 md:px-5 md:py-4 border-b border-slate-200/80 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0a4d29] to-emerald-700 flex items-center justify-center shadow shrink-0">
            <Target size={22} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-bold text-slate-900 truncate">تعیین دامنه قیمت متناسب</h1>
            <p className="text-xs md:text-sm text-slate-600 truncate">
              انتخاب پروژه · دستورالعمل · بهنگام · قیمت‌ها — تشخیص برنده و دستیار هوشمند
            </p>
          </div>
        </div>
      </div>

      {/* بدنه سه‌ستونه — وسط عریض‌تر برای نتایج، دستیار جمع‌وجورتر */}
      <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,2.1fr)_minmax(0,1.05fr)] overflow-y-auto lg:overflow-hidden">
        {/* ستون ورودی */}
        <div className="shrink-0 lg:min-h-0 lg:overflow-y-auto border-b lg:border-b-0 lg:border-l border-slate-200/80 p-4 space-y-4 bg-white/50">
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-4">
              <h2 className="font-bold text-slate-800 flex items-center gap-2 text-sm">
                <BookOpen size={16} /> تنظیمات
              </h2>

              <ProjectSearchSelect
                projects={projects}
                value={projectId}
                onChange={handleProjectChange}
                label="پروژه"
                placeholder="جستجو: نام، شماره قرارداد، وضعیت..."
              />

              {(analyses.length > 0 || contextLoading) && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">آنالیز (برای پر کردن قیمت من)</label>
                  {contextLoading ? (
                    <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                      <Loader2 size={14} className="animate-spin text-emerald-600" />
                      در حال بارگذاری آنالیز...
                    </div>
                  ) : (
                    <select
                      value={analysisId}
                      onChange={(e) => handleAnalysisChange(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
                    >
                      <option value="">— اختیاری —</option>
                      {analyses.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} — {formatDisplayNumber(a.total_cost)}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">دستورالعمل</label>
                <select
                  value={guidelineId}
                  onChange={(e) => setGuidelineId(Number(e.target.value))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  {guidelines.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                {selectedGuideline?.description && (
                  <p className="text-[11px] text-slate-500 mt-1">{selectedGuideline.description}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">برآورد بهنگام (Po)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberInput(Po)}
                  onChange={(e) => setPo(parseNumberInput(e.target.value))}
                  placeholder="مبلغ بهنگام مناقصه"
                  className="w-full min-w-0 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-emerald-500/30 tracking-tight"
                  dir="ltr"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-slate-800 text-sm">قیمت پیشنهادها</h2>
                <button
                  type="button"
                  onClick={() => setBidders((p) => [...p, emptyBidder()])}
                  className="text-xs font-bold text-emerald-700 flex items-center gap-1 hover:underline"
                >
                  <Plus size={14} /> افزودن
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">قیمت خود را با «من» مشخص کنید. با انتخاب پروژه، قیمت آنالیز و نام شرکت در ردیف شما پر می‌شود.</p>
              <div className="space-y-3">
                {bidders.map((b, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border p-2.5 space-y-2 ${b.is_self ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-100 bg-white'}`}
                  >
                    <div className="flex gap-2 items-center min-w-0">
                      <button
                        type="button"
                        onClick={() => setSelfBidder(i)}
                        title="قیمت من"
                        className={`shrink-0 w-8 h-8 rounded-lg text-xs font-bold ${b.is_self ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-500'}`}
                      >
                        من
                      </button>
                      <input
                        type="text"
                        placeholder="نام (اختیاری)"
                        value={b.name}
                        onChange={(e) => updateBidder(i, { name: e.target.value })}
                        className="flex-1 min-w-0 px-2 py-2 border border-slate-200 rounded-lg text-sm outline-none"
                      />
                      {bidders.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setBidders((p) => p.filter((_, idx) => idx !== i))}
                          className="shrink-0 p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="قیمت (ریال)"
                      value={formatNumberInput(b.price)}
                      onChange={(e) => updateBidder(i, { price: parseNumberInput(e.target.value) })}
                      className="w-full min-w-0 px-2.5 py-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-emerald-500/30 tracking-tight"
                      dir="ltr"
                    />
                  </div>
                ))}
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 flex gap-2">
                <AlertTriangle size={18} className="shrink-0" />
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-2 sticky bottom-0 pb-1">
              <button
                type="button"
                onClick={handleCalculate}
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0a4d29] text-white font-bold text-sm disabled:opacity-50 shadow-sm"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Calculator size={18} />}
                محاسبه دامنه و برنده
              </button>
              {result && (
                <button
                  type="button"
                  onClick={handleAiAdvice}
                  disabled={aiLoading}
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 font-bold text-sm disabled:opacity-50"
                >
                  {aiLoading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  راهنمای برد
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ستون نتایج */}
        <div className="min-w-0 lg:min-h-0 lg:overflow-y-auto p-4 space-y-4 bg-slate-50/30 relative">
          {loading && (
            <div className="absolute inset-x-4 top-4 z-10 flex items-center gap-2 rounded-lg border border-emerald-200 bg-white/95 px-3 py-2 text-xs text-emerald-800 shadow-sm">
              <Loader2 size={14} className="animate-spin shrink-0" />
              در حال محاسبه...
            </div>
          )}
          {!result ? (
            <div className="min-h-[140px] flex flex-col items-center justify-center bg-white rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500">
              <Target size={28} className="mb-3 opacity-25" />
              <p className="text-sm font-medium">پس از محاسبه، مسیر ماده ۶ و برنده اینجا نمایش داده می‌شود.</p>
              <p className="text-xs mt-2 text-slate-400">یا از دستیار هوشمند سمت چپ قیمت‌های نمونه بگیرید.</p>
            </div>
          ) : (
            <>
              <div className={`rounded-xl border p-4 ${result.needs_committee_review ? 'border-amber-300 bg-amber-50' : result.needs_reestimate ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50/40'}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200">{result.article}</span>
                  <h3 className="font-bold text-slate-900">{result.article_title}</h3>
                  {result.normalization_applied === false && result.article !== '6-2' && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">بدون نرمال‌سازی</span>
                  )}
                  {result.normalization_applied && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">نرمال‌سازی اعمال شد</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{result.summary}</p>
                {result.needs_committee_review && (
                  <div className="mt-3 rounded-lg bg-amber-100/80 border border-amber-300 px-3 py-2 text-xs text-amber-950 flex items-start gap-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>طبق ماده ۶-۲ موضوع به کمیسیون ارجاع می‌شود؛ برنده صرفاً با فرمول تعیین نمی‌شود.</span>
                  </div>
                )}
                {result.article === '6-2' && result.mean_ratio_to_po_pct != null && (
                  <div className="mt-3 rounded-lg bg-amber-100/80 border border-amber-300 px-3 py-2 text-xs text-amber-950 leading-relaxed">
                    <strong>علت:</strong> میانگین پیشنهادها {result.mean_ratio_to_po_pct}٪ بهنگام است
                    {result.mean_valid_pct_range
                      ? ` (باید ${result.mean_valid_pct_range.min}٪ تا ${result.mean_valid_pct_range.max}٪ باشد).`
                      : ' (باید ۸۰٪ تا ۱۳۵٪ باشد).'}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div className="bg-white/80 rounded-lg p-2">
                    <span className="text-slate-500 block">بهنگام (Po)</span>
                    <span className="font-mono font-bold">{formatDisplayNumber(result.Po)}</span>
                  </div>
                  <div className="bg-white/80 rounded-lg p-2">
                    <span className="text-slate-500 block">تعداد پیشنهاد</span>
                    <span className="font-bold">{result.n}{result.n_total != null ? ` (کل: ${result.n_total})` : ''}</span>
                  </div>
                  {result.mean != null && (
                    <div className="bg-white/80 rounded-lg p-2">
                      <span className="text-slate-500 block">میانگین Pi</span>
                      <span className="font-mono font-bold">{formatDisplayNumber(result.mean)}</span>
                      {result.mean_ratio_to_po_pct != null && (
                        <span className="block text-slate-500 mt-0.5">{result.mean_ratio_to_po_pct}٪ بهنگام</span>
                      )}
                    </div>
                  )}
                  {result.mo != null && (
                    <div className="bg-white/80 rounded-lg p-2">
                      <span className="text-slate-500 block">mo (Po + Pi)</span>
                      <span className="font-mono font-bold">{formatDisplayNumber(result.mo)}</span>
                    </div>
                  )}
                  {result.std_dev != null && (
                    <div className="bg-white/80 rounded-lg p-2">
                      <span className="text-slate-500 block">انحراف معیار (so)</span>
                      <span className="font-mono font-bold">{formatDisplayNumber(result.std_dev)}</span>
                    </div>
                  )}
                  {result.Po_normalized != null && (
                    <div className="bg-white/80 rounded-lg p-2">
                      <span className="text-slate-500 block">Po&apos; (نرمال)</span>
                      <span className="font-mono font-bold">{result.Po_normalized.toFixed(4)}</span>
                      {result.Po_in_norm_domain != null && (
                        <span className="block text-slate-500 mt-0.5">
                          {result.Po_in_norm_domain ? 'در دامنه [−۱,+۱]' : 'خارج دامنه نرمال'}
                        </span>
                      )}
                    </div>
                  )}
                  {result.initial_range && (
                    <div className="bg-white/80 rounded-lg p-2 col-span-2">
                      <span className="text-slate-500 block">بازه ±۱۰٪ Po</span>
                      <span className="font-mono text-sm">{formatDisplayNumber(result.initial_range.min)} — {formatDisplayNumber(result.initial_range.max)}</span>
                    </div>
                  )}
                  {result.exception_pct != null && result.normalization_applied && (
                    <div className="bg-white/80 rounded-lg p-2 col-span-2">
                      <span className="text-slate-500 block">استثنای خارج دامنه نرمال</span>
                      <span className="text-sm">±{result.exception_pct}٪ نسبت به Po</span>
                    </div>
                  )}
                </div>
              </div>

              {result.winner && (
                <div className="bg-white rounded-xl border-2 border-emerald-400 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Trophy size={20} className="text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-xs text-emerald-700 font-bold">برنده اول — کمترین قیمت متناسب (ماده ۷)</p>
                    <p className="font-bold text-slate-900">{result.winner.name}{result.winner.is_self ? ' (شما)' : ''}</p>
                    <p className="font-mono text-emerald-800">{formatDisplayNumber(result.winner.price)} ریال</p>
                  </div>
                </div>
              )}

              {result.second_winner && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <Trophy size={18} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 font-bold">برنده دوم — دومین کمترین متناسب</p>
                    <p className="font-bold text-slate-900">{result.second_winner.name}{result.second_winner.is_self ? ' (شما)' : ''}</p>
                    <p className="font-mono text-slate-700">{formatDisplayNumber(result.second_winner.price)} ریال</p>
                  </div>
                </div>
              )}

              {result.bidders.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 font-bold text-sm">جدول پیشنهادها</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[360px]">
                      <thead>
                        <tr className="text-right text-xs text-slate-500 border-b">
                          <th className="p-3">نام</th>
                          <th className="p-3">قیمت</th>
                          <th className="p-3">٪ بهنگام</th>
                          {result.normalization_applied && <th className="p-3">P&apos;i</th>}
                          <th className="p-3">وضعیت</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.bidders.map((b, i) => (
                          <tr key={i} className={`border-b border-slate-50 ${b.is_self ? 'bg-emerald-50/30' : ''} ${result.winner?.price === b.price && result.winner?.name === b.name ? 'ring-1 ring-inset ring-emerald-300' : ''}`}>
                            <td className="p-3 font-medium">{b.name}{b.is_self ? ' ★' : ''}</td>
                            <td className="p-3 font-mono">{formatDisplayNumber(b.price)}</td>
                            <td className="p-3">{b.ratio_to_po_pct != null ? `${b.ratio_to_po_pct}٪` : '—'}</td>
                            {result.normalization_applied && (
                              <td className="p-3 font-mono text-xs">{b.normalized != null ? b.normalized.toFixed(4) : '—'}</td>
                            )}
                            <td className="p-3">{renderDomainStatus(b, result)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {result.regulation_notes && result.regulation_notes.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h4 className="font-bold text-slate-800 text-sm mb-2">یادداشت‌های بخشنامه</h4>
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {result.regulation_notes.map((t, i) => (
                      <li key={i} className="flex gap-2"><span className="text-emerald-600">•</span>{t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {aiReply && (
                <div className="bg-white border border-violet-200 rounded-xl p-4">
                  <h4 className="font-bold text-violet-900 text-sm mb-2 flex items-center gap-2">
                    <Sparkles size={16} /> مشاوره هوشمند
                  </h4>
                  <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{aiReply}</p>
                </div>
              )}
            </>
          )}

          <details className="bg-white border border-slate-200 rounded-xl">
            <summary className="px-4 py-3 cursor-pointer font-bold text-sm text-slate-700 flex items-center gap-2 list-none">
              <ChevronDown size={16} />
              خلاصه قوانین دستورالعمل
            </summary>
            <div className="px-4 pb-4 text-xs text-slate-600 space-y-2 leading-relaxed border-t border-slate-100 pt-3">
              <p><strong>مرحله ۱:</strong> n کل (Po + Pi) &lt; ۴ → ماده ۶-۱، بازه ±۱۰٪ Po.</p>
              <p><strong>مرحله ۲:</strong> همه در ±۱۰٪ → بدون نرمال‌سازی، کمترین = برنده.</p>
              <p><strong>مرحله ۵:</strong> میانگین Pi باید ۸۰٪–۱۳۵٪ Po؛ وگرنه ماده ۶-۲ و ارجاع کمیسیون.</p>
              <p><strong>مراحل ۶–۱۰:</strong> mo/so از مجموعه Po و Pi؛ P&apos;i و Po&apos;؛ متناسب = [−۱,+۱] یا استثنای ±۲۰٪/±۱۰٪.</p>
              <p><strong>ماده ۷:</strong> برنده اول = کمترین متناسب؛ دوم = دومین کمترین.</p>
            </div>
          </details>
        </div>

        {/* ستون دستیار AI */}
        <div className="shrink-0 lg:min-h-0 lg:overflow-y-auto p-4 bg-slate-50/50 border-t lg:border-t-0 lg:border-r border-slate-200/80">
          <BidDomainAiPanel
            className="h-[min(420px,55vh)] lg:h-full"
            guidelineId={guidelineId}
            Po={Po}
            bidders={bidders}
            result={result}
            projectId={projectId}
            projectName={selectedProject?.original_name}
            onApplyBidders={applyAiBidders}
          />
        </div>
      </div>
    </div>
  );
};

export default BidDomainPage;
